import type { NavProgress } from '../core/types';
import type { UnitSystem } from '../core/settings';
import { formatDistance } from '../core/format';
import { getLang } from '../core/i18n';
import { illustratedJunction } from '../nav/junctionDecision';
import { laneMoveSentence } from '../nav/phrasing';
import { roadStripLabel } from '../nav/roads';
import { laneValid } from '../nav/lanes';

const esc = (s: string) => s.replace(/[&<>"']/g, c => `&#${c.charCodeAt(0)};`);

export class JunctionGuide {
  readonly el = document.createElement('section');
  private key = '';
  constructor(private parent: HTMLElement) {
    this.el.className = 'junction-guide';
    this.el.hidden = true;
    this.el.setAttribute('aria-label', 'Junction guidance');
    parent.append(this.el);
  }
  hide() {
    this.el.hidden = true;
    this.parent.classList.remove('has-junction-guide');
    this.key = '';
  }
  update(progress: NavProgress, units: UnitSystem, speed: number) {
    const d = illustratedJunction(progress, speed);
    if (!d) { this.hide(); return; }
    const lang = getLang();
    const th = lang === 'th';
    // Null advice means the lane model failed the direction cross-check.
    const lanes = d.advice && d.advice.validCount > 0 ? d.lanes : null;
    const mod = d.step?.maneuver.modifier ?? '';
    const side = mod.includes('left') ? -1 : mod.includes('right') ? 1 : 0;
    const exit = d.step?.exits?.trim();
    const action = !d.step && !lanes ? (th ? 'ดูป้ายจราจร' : 'Follow road signs') : side < 0 ? (th ? 'ชิดซ้าย' : 'Keep left') : side > 0 ? (th ? 'ชิดขวา' : 'Keep right') : (th ? 'ตรงไป' : 'Continue ahead');
    const title = lanes ? laneMoveSentence(d.advice, lang) || action : action;
    const road = roadStripLabel(progress.step, lang);
    const destination = roadStripLabel(d.step, lang);
    const label = exit ? `${th ? 'ทางออก' : 'Exit'} ${exit}` : th ? 'ทางแยกข้างหน้า' : 'Junction ahead';
    const dist = formatDistance(d.distance, units);
    const key = JSON.stringify([lanes, mod, title, road, destination, label, lang]);
    this.el.hidden = false;
    this.parent.classList.add('has-junction-guide');
    if (key !== this.key) {
      this.key = key;
      const count = lanes?.length ?? 1;
      const gap = Math.min(68, 440 / count);
      const start = 320 - (count - 1) * gap / 2;
      const ribbons = Array.from({ length: count }, (_, i) => {
        const lane = lanes?.[i];
        const valid = lane ? laneValid(lane) : true;
        const x = start + i * gap;
        const signs = lane?.indications ?? [];
        const sign = valid ? lane?.valid_indication ?? (signs.length === 1 ? signs[0] : mod) : signs.length === 1 ? signs[0] : '';
        // Lane positions are supplied left-to-right. Direction is schematic,
        // never an inferred lane-to-lane connection across the junction.
        const direction = valid && side ? side : sign.includes('left') ? -1 : sign.includes('right') ? 1 : 0;
        const shift = direction * 104;
        const path = `M ${x} 252 L ${x} 165 Q ${x} 120 ${x + shift * .5} 90 L ${x + shift} 36`;
        const width = gap * .83;
        const routes = valid ? `<path d="${path}" class="junction-route" stroke-width="${Math.min(16, gap * .24)}"/><path d="M -12 12 L 0 0 L 12 12" transform="translate(${x + shift} 36) rotate(${direction * 43})" class="junction-route" stroke-width="7"/>` : '';
        return `<g data-lane="${i + 1}" data-valid="${valid}"><path d="${path}" class="junction-road-edge" stroke-width="${width + 4}"/><path d="${path}" class="junction-road ${valid ? 'is-route' : ''}" stroke-width="${width}"/>${routes}${lanes ? `<circle cx="${x}" cy="235" r="${Math.min(21, gap * .39)}" class="junction-number ${valid ? 'is-route' : ''}"/><text x="${x}" y="244" text-anchor="middle" class="junction-lane-number">${i + 1}</text>` : ''}</g>`;
      }).join('');
      this.el.innerHTML = `<header class="junction-guide__header"><div><span class="junction-guide__eyebrow">${esc(label)}</span><h2>${esc(title)}</h2></div><strong class="junction-guide__distance"></strong></header>
        <div class="junction-guide__roads"><span>${esc(th ? 'ถนนปัจจุบัน' : 'Current road')} <b>${esc(road || '—')}</b></span>${destination && destination !== road ? `<span>→ <b>${esc(destination)}</b></span>` : ''}</div>
        <svg class="junction-guide__drawing" viewBox="${start - 145} 0 ${(count - 1) * gap + 290} 280" role="img" aria-label="${esc(title)}"><title>${esc(title)}</title>${ribbons}</svg>
        <footer><span class="junction-guide__legend"><i></i>${esc(th ? 'เส้นทางของคุณ' : 'Your route')}</span><span>${esc(lanes ? (th ? 'แผนภาพช่องทาง • นับจากซ้าย' : 'Lane schematic · numbered from left') : (th ? 'ไม่มีข้อมูลช่องทาง' : 'Lane data unavailable'))}</span></footer>`;
    }
    // Only the countdown changes on GPS updates; keep the illustrated geometry still.
    const distance = this.el.querySelector('.junction-guide__distance')!;
    if (distance.textContent !== dist) distance.textContent = dist;
  }
}
